import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

class AraProductOrder extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.currentVariant = null;
    this.submitButton = null;
    this.mobileButton = null;
    this.message = null;
    this.currency = this.dataset.currency || 'USD';

    this.numberFormatter = new Intl.NumberFormat(
      document.documentElement.lang || 'en-US',
      {
        style: 'currency',
        currency: this.currency,
      }
    );
  }

  connectedCallback() {
    const variantData = this.querySelector('[data-ara-variants]');

    try {
      this.variants = JSON.parse(
        variantData?.textContent || '[]'
      );
    } catch (error) {
      console.error(
        '[Ara product order] Variant data could not be read.',
        error
      );
      return;
    }

    this.form = this.querySelector(
      '[data-ara-product-form]'
    );

    this.submitButton = this.querySelector(
      '[data-ara-submit]'
    );

    this.mobileButton = this.querySelector(
      '[data-ara-mobile-submit]'
    );

    this.message = this.querySelector(
      '[data-ara-message]'
    );

    this.quantityInput = this.querySelector(
      '[data-quantity-input]'
    );

    this.totalElements = this.querySelectorAll(
      '[data-ara-total], [data-ara-mobile-total]'
    );

    if (!this.form || !this.submitButton) return;

    const initialId = Number(
      this.querySelector(
        '[data-ara-variant-id]'
      )?.value || 0
    );

    this.currentVariant =
      this.variants.find(
        (variant) =>
          Number(variant.id) === initialId
      ) ||
      this.variants[0] ||
      null;

    this.bindOptions();
    this.bindGallery();
    this.bindQuantity();
    this.bindAddons();
    this.bindPickupDate();
    this.bindPartyBox();
    this.bindZoom();
    this.bindMobileBar();

    this.form.addEventListener(
      'submit',
      (event) => this.handleSubmit(event)
    );

    this.mobileButton?.addEventListener(
      'click',
      () => this.form.requestSubmit()
    );

    this.updateVariantState(false);
    this.updateTotal();
  }

  bindOptions() {
    this.querySelectorAll(
      '[data-ara-option-input]'
    ).forEach((input) => {
      input.addEventListener(
        'change',
        () => {
          const selected =
            this.getSelectedOptions();

          this.currentVariant =
            this.findVariant(selected) ||
            null;

          this.updateVariantState(true);
        }
      );
    });
  }

  getSelectedOptions() {
    const values = [];

    this.querySelectorAll(
      '.ara-product-order__option[data-option-index]'
    ).forEach((fieldset) => {
      const index = Number(
        fieldset.dataset.optionIndex
      );

      const checked =
        fieldset.querySelector(
          '[data-ara-option-input]:checked'
        );

      values[index] =
        checked?.value || '';
    });

    return values;
  }

  findVariant(options) {
    return this.variants.find(
      (variant) =>
        variant.options.every(
          (option, index) =>
            option === options[index]
        )
    );
  }

  updateVariantState(
    updateUrl = true
  ) {
    const selected =
      this.getSelectedOptions();

    if (selected.length) {
      this.currentVariant =
        this.findVariant(selected) ||
        null;
    }

    const hiddenId =
      this.querySelector(
        '[data-ara-variant-id]'
      );

    const price =
      this.querySelector(
        '[data-ara-price]'
      );

    const compare =
      this.querySelector(
        '[data-ara-compare-price]'
      );

    const availability =
      this.querySelector(
        '[data-ara-availability]'
      );

    const submitLabel =
      this.querySelector(
        '[data-submit-label]'
      );

    this.querySelectorAll(
      '.ara-product-order__option[data-option-index]'
    ).forEach((fieldset) => {
      const selectedInput =
        fieldset.querySelector(
          '[data-ara-option-input]:checked'
        );

      const selectedLabel =
        fieldset.querySelector(
          '[data-selected-value]'
        );

      if (selectedLabel) {
        selectedLabel.textContent =
          selectedInput?.value || '';
      }
    });

    this.updateUnavailableOptions();

    if (!this.currentVariant) {
      if (hiddenId) {
        hiddenId.value = '';
      }

      if (submitLabel) {
        submitLabel.textContent =
          'Unavailable';
      }

      if (availability) {
        availability.textContent =
          'Combination unavailable';

        availability.classList.add(
          'is-unavailable'
        );
      }

      this.submitButton.disabled = true;

      if (this.mobileButton) {
        this.mobileButton.disabled = true;
      }

      this.updateTotal();
      return;
    }

    if (hiddenId) {
      hiddenId.value =
        this.currentVariant.id;
    }

    if (price) {
      price.textContent =
        this.formatMoney(
          this.currentVariant.price
        );
    }

    if (compare) {
      const showCompare =
        Number(
          this.currentVariant
            .compare_at_price
        ) >
        Number(
          this.currentVariant.price
        );

      compare.hidden =
        !showCompare;

      compare.textContent =
        showCompare
          ? this.formatMoney(
              this.currentVariant
                .compare_at_price
            )
          : '';
    }

    const available =
      Boolean(
        this.currentVariant.available
      );

    if (availability) {
      availability.textContent =
        available
          ? 'Available to order'
          : 'Currently unavailable';

      availability.classList.toggle(
        'is-unavailable',
        !available
      );
    }

    if (submitLabel) {
      submitLabel.textContent =
        available
          ? 'Add to order'
          : 'Unavailable';
    }

    this.submitButton.disabled =
      !available;

    if (this.mobileButton) {
      this.mobileButton.disabled =
        !available;
    }

    const variantMediaId =
      this.currentVariant
        .featured_media?.id ||
      this.currentVariant
        .featured_image?.id;

    if (variantMediaId) {
      this.showMedia(
        variantMediaId
      );
    }

    if (
      updateUrl &&
      window.history?.replaceState
    ) {
      const url =
        new URL(
          window.location.href
        );

      url.searchParams.set(
        'variant',
        this.currentVariant.id
      );

      window.history.replaceState(
        {},
        '',
        url
      );
    }

    this.updateTotal();
  }

  updateUnavailableOptions() {
    this.querySelectorAll(
      '.ara-product-order__option[data-option-index]'
    ).forEach((fieldset) => {
      const optionIndex =
        Number(
          fieldset.dataset.optionIndex
        );

      fieldset
        .querySelectorAll(
          '[data-ara-option-input]'
        )
        .forEach((input) => {
          const existsInAvailableVariant =
            this.variants.some(
              (variant) => {
                return (
                  variant.available &&
                  variant.options[
                    optionIndex
                  ] === input.value
                );
              }
            );

          input.disabled =
            !existsInAvailableVariant;
        });
    });
  }

  bindGallery() {
    this.querySelectorAll(
      '[data-ara-thumb]'
    ).forEach((button) => {
      button.addEventListener(
        'click',
        () =>
          this.showMedia(
            button.dataset.araThumb
          )
      );
    });
  }

  showMedia(mediaId) {
    if (!mediaId) return;

    this.querySelectorAll(
      '[data-media-id]'
    ).forEach((media) => {
      const active =
        String(
          media.dataset.mediaId
        ) === String(mediaId);

      media.hidden = !active;

      media.classList.toggle(
        'is-active',
        active
      );

      if (!active) {
        media
          .querySelectorAll('video')
          .forEach((video) =>
            video.pause()
          );
      }
    });

    this.querySelectorAll(
      '[data-ara-thumb]'
    ).forEach((thumb) => {
      const active =
        String(
          thumb.dataset.araThumb
        ) === String(mediaId);

      thumb.classList.toggle(
        'is-active',
        active
      );

      thumb.setAttribute(
        'aria-pressed',
        active ? 'true' : 'false'
      );
    });
  }

  bindQuantity() {
    this.querySelector(
      '[data-quantity-minus]'
    )?.addEventListener(
      'click',
      () => this.changeQuantity(-1)
    );

    this.querySelector(
      '[data-quantity-plus]'
    )?.addEventListener(
      'click',
      () => this.changeQuantity(1)
    );

    this.quantityInput?.addEventListener(
      'input',
      () => {
        const value = Math.max(
          1,
          Math.min(
            20,
            Number(
              this.quantityInput.value
            ) || 1
          )
        );

        this.quantityInput.value =
          String(value);

        this.updateTotal();
      }
    );
  }

  changeQuantity(change) {
    if (!this.quantityInput) return;

    const next = Math.max(
      1,
      Math.min(
        20,
        Number(
          this.quantityInput.value ||
            1
        ) + change
      )
    );

    this.quantityInput.value =
      String(next);

    this.updateTotal();
  }

  bindAddons() {
    this.querySelectorAll(
      '[data-ara-addon-checkbox], [data-ara-addon-variant]'
    ).forEach((control) => {
      control.addEventListener(
        'change',
        () => this.updateTotal()
      );
    });
  }

  bindPickupDate() {
    const input =
      this.querySelector(
        '[data-ara-pickup-date]'
      );

    if (!input) return;

    const earliest = Number(
      this.dataset.earliestDays || 0
    );

    const advance = Number(
      this.dataset.advanceDays || 60
    );

    input.min =
      this.toDateInputValue(
        this.addDays(
          new Date(),
          earliest
        )
      );

    input.max =
      this.toDateInputValue(
        this.addDays(
          new Date(),
          advance
        )
      );
  }

  addDays(date, amount) {
    const copy = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    copy.setDate(
      copy.getDate() + amount
    );

    return copy;
  }

  toDateInputValue(date) {
    const year =
      date.getFullYear();

    const month =
      String(
        date.getMonth() + 1
      ).padStart(2, '0');

    const day =
      String(
        date.getDate()
      ).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  bindPartyBox() {
    this.querySelectorAll(
      '[data-max-choices]'
    ).forEach((group) => {
      group
        .querySelectorAll(
          'input[type="checkbox"]'
        )
        .forEach((input) => {
          input.addEventListener(
            'change',
            () => {
              const max = Number(
                group.dataset
                  .maxChoices || 0
              );

              const checked = [
                ...group.querySelectorAll(
                  'input[type="checkbox"]:checked'
                ),
              ];

              if (
                checked.length > max
              ) {
                input.checked =
                  false;
              }

              group
                .querySelector(
                  '[data-group-error]'
                )
                ?.setAttribute(
                  'hidden',
                  ''
                );
            }
          );
        });
    });
  }

  validateCustomGroups() {
    let valid = true;

    this.querySelectorAll(
      '[data-required-group="true"]'
    ).forEach((group) => {
      const checked =
        group.querySelectorAll(
          'input[type="checkbox"]:checked'
        ).length;

      const max = Number(
        group.dataset.maxChoices ||
          999
      );

      const groupValid =
        checked >= 1 &&
        checked <= max;

      const error =
        group.querySelector(
          '[data-group-error]'
        );

      if (error) {
        error.hidden =
          groupValid;
      }

      if (!groupValid) {
        valid = false;
      }
    });

    return valid;
  }

  bindZoom() {
    const dialog =
      this.querySelector(
        '[data-ara-zoom-dialog]'
      );

    const image =
      dialog?.querySelector(
        '[data-ara-zoom-image]'
      );

    this.querySelectorAll(
      '[data-ara-zoom]'
    ).forEach((trigger) => {
      trigger.addEventListener(
        'click',
        () => {
          if (
            !dialog ||
            !image
          ) {
            return;
          }

          image.src =
            trigger.dataset
              .zoomSrc || '';

          dialog.showModal();
        }
      );
    });

    dialog
      ?.querySelector(
        '[data-ara-zoom-close]'
      )
      ?.addEventListener(
        'click',
        () => dialog.close()
      );

    dialog?.addEventListener(
      'click',
      (event) => {
        if (
          event.target === dialog
        ) {
          dialog.close();
        }
      }
    );
  }

  bindMobileBar() {
    const bar =
      this.querySelector(
        '[data-ara-mobile-bar]'
      );

    if (
      !bar ||
      !this.submitButton ||
      !(
        'IntersectionObserver' in
        window
      )
    ) {
      return;
    }

    bar.hidden = false;

    const observer =
      new IntersectionObserver(
        ([entry]) => {
          bar.classList.toggle(
            'is-visible',
            !entry.isIntersecting
          );
        },
        {
          threshold: 0.15,
        }
      );

    observer.observe(
      this.submitButton
    );
  }

  collectProperties() {
    const properties = {};
    const grouped = new Map();

    this.querySelectorAll(
      '[data-ara-property]'
    ).forEach((field) => {
      const name =
        field.dataset.araProperty;

      if (!name) return;

      if (
        field.type ===
        'checkbox'
      ) {
        if (!field.checked) return;

        const values =
          grouped.get(name) || [];

        values.push(field.value);

        grouped.set(
          name,
          values
        );

        return;
      }

      if (
        field.type === 'radio'
      ) {
        if (field.checked) {
          properties[name] =
            field.value;
        }

        return;
      }

      const value =
        field.value?.trim();

      if (value) {
        properties[name] =
          value;
      }
    });

    grouped.forEach(
      (values, name) => {
        properties[name] =
          values.join(', ');
      }
    );

    return properties;
  }

  getSelectedAddons() {
    return [
      ...this.querySelectorAll(
        '[data-ara-addon-card]'
      ),
    ]
      .filter((card) => {
        return card.querySelector(
          '[data-ara-addon-checkbox]'
        )?.checked;
      })
      .map((card) => {
        const variantField =
          card.querySelector(
            '[data-ara-addon-variant]'
          );

        const selectedOption =
          variantField?.tagName ===
          'SELECT'
            ? variantField
                .selectedOptions[0]
            : variantField;

        return {
          id: Number(
            variantField?.value ||
              0
          ),
          quantity: 1,
          price: Number(
            selectedOption?.dataset
              .price || 0
          ),
          title:
            selectedOption?.dataset
              .title || '',
        };
      })
      .filter(
        (item) =>
          item.id > 0
      );
  }

  updateTotal() {
    const quantity = Math.max(
      1,
      Number(
        this.quantityInput?.value ||
          1
      )
    );

    const productTotal =
      Number(
        this.currentVariant
          ?.price || 0
      ) * quantity;

    const addonsTotal =
      this.getSelectedAddons().reduce(
        (sum, item) =>
          sum + item.price,
        0
      );

    const total =
      productTotal +
      addonsTotal;

    this.totalElements.forEach(
      (element) => {
        element.textContent =
          this.formatMoney(total);
      }
    );
  }

  formatMoney(cents) {
    return this.numberFormatter.format(
      Number(cents || 0) / 100
    );
  }

  setMessage(
    text,
    success = false
  ) {
    if (!this.message) return;

    this.message.textContent =
      text;

    this.message.classList.toggle(
      'is-success',
      success
    );
  }

  setLoading(loading) {
    this.submitButton.classList.toggle(
      'is-loading',
      loading
    );

    this.submitButton.disabled =
      loading ||
      !this.currentVariant
        ?.available;

    if (this.mobileButton) {
      this.mobileButton.disabled =
        loading ||
        !this.currentVariant
          ?.available;
    }

    const label =
      this.submitButton.querySelector(
        '[data-submit-label]'
      );

    if (label) {
      label.textContent =
        loading
          ? 'Adding…'
          : this.currentVariant
                ?.available
            ? 'Add to order'
            : 'Unavailable';
    }
  }

  async handleSubmit(event) {
    event.preventDefault();

    this.setMessage('');

    if (
      !this.currentVariant
        ?.available
    ) {
      this.setMessage(
        'Please choose an available option.'
      );
      return;
    }

    const pickupCalendar =
      this.querySelector(
        'ara-pickup-calendar'
      );

    if (
      pickupCalendar &&
      typeof pickupCalendar
        .validate === 'function' &&
      !pickupCalendar.validate(true)
    ) {
      this.setMessage(
        'Please complete your pickup details.'
      );
      return;
    }

    if (
      !this.form.checkValidity()
    ) {
      this.form.reportValidity();
      return;
    }

    if (
      !this.validateCustomGroups()
    ) {
      this.setMessage(
        'Please complete the Party Box selections.'
      );

      this.querySelector(
        '[data-ara-party-box]'
      )?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      return;
    }

    const quantity = Math.max(
      1,
      Number(
        this.quantityInput?.value ||
          1
      )
    );

    const properties =
      this.collectProperties();

    const addons =
      this.getSelectedAddons();

    const items = [
      {
        id: Number(
          this.currentVariant.id
        ),
        quantity,
        properties,
      },

      ...addons.map(
        (addon) => ({
          id: addon.id,
          quantity:
            addon.quantity,
          properties: {
            '_Added with':
              this.dataset
                .productTitle || '',
          },
        })
      ),
    ];

    const sectionIds = [
      ...new Set(
        [
          ...document.querySelectorAll(
            'cart-items-component[data-section-id]'
          ),
        ]
          .map(
            (component) =>
              component.dataset
                .sectionId
          )
          .filter(Boolean)
      ),
    ];

    const deferred =
      CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',

        lines: items.map(
          (item) => ({
            merchandiseId:
              String(item.id),

            quantity:
              item.quantity,
          })
        ),

        promise:
          deferred.promise,
      })
    );

    this.setLoading(true);

    try {
      const response =
        await fetch(
          Theme.routes
            .cart_add_url,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json',
            },

            body: JSON.stringify({
              items,

              sections:
                sectionIds.join(
                  ','
                ),
            }),
          }
        );

      const result =
        await response.json();

      if (
        !response.ok ||
        result.status
      ) {
        throw new Error(
          result.description ||
            result.message ||
            'This item could not be added.'
        );
      }

      const cartResponse =
        await fetch(
          `${Theme.routes.cart_url}.json`,
          {
            headers: {
              Accept:
                'application/json',
            },

            credentials:
              'same-origin',
          }
        );

      if (!cartResponse.ok) {
        throw new Error(
          'The cart could not be refreshed.'
        );
      }

      const cart =
        await cartResponse.json();

      deferred.resolve({
        cart:
          CartLinesUpdateEvent
            .createCartFromAjaxResponse(
              cart
            ),

        detail: {
          items: cart.items,

          sections:
            result.sections,

          source:
            'ara-product-order',

          sourceId:
            this.id,

          itemCount:
            items.reduce(
              (sum, item) =>
                sum +
                item.quantity,
              0
            ),

          productId:
            this.dataset.productId,

          didError: false,
        },
      });

      this.setMessage(
        addons.length
          ? 'Your dessert and selected extras were added.'
          : 'Added to your order.',
        true
      );

      window.setTimeout(
        () => {
          const drawer =
            document.querySelector(
              '#cart-drawer'
            );

          if (
            drawer &&
            !drawer.hasAttribute(
              'open'
            ) &&
            typeof drawer.open ===
              'function'
          ) {
            drawer.open();
          }
        },
        60
      );
    } catch (error) {
      deferred.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error:
            error?.message ||
            'Add to cart failed',

          code: 'INVALID',
        })
      );

      this.setMessage(
        error?.message ||
          'Something went wrong. Please try again.'
      );
    } finally {
      this.setLoading(false);
    }
  }
}

if (
  !customElements.get(
    'ara-product-order'
  )
) {
  customElements.define(
    'ara-product-order',
    AraProductOrder
  );
}
